import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Add, Close, Language, LocationOn, Search } from '@material-ui/icons';
import {
  ACTIVITY_COLUMNS,
  ColumnDef,
  getColumnOptions,
  getOneOffColumns,
  getSessionColumns,
  isColumnApplicable,
  NAME_COLUMN,
  ONE_OFF_TYPES,
} from '../activity-import-columns';
import { ImportActivityRow, RowErrors } from '../activity-import-types';
import { getRowOneOffCount, getRowSessionCount, parseActivityType } from '../activity-import-validation';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { getRowStatus, StatusIcon } from './status-icon';
import { BUTTON_ICON_STYLE, getPrimaryButtonStyle, getToolbarButtonStyle } from '../import-button-styles';

const useStyles = makeStyles((theme: Theme) => ({
  drawer: {
    width: 380,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    border: `1px solid ${theme.color.grey.border}`,
    borderRadius: 6,
    boxShadow: '-8px 0 16px -12px rgba(0, 0, 0, 0.15)',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '18px 20px 14px',
    '& > * + *': {
      marginLeft: 8,
    },
    borderBottom: `1px solid ${theme.color.grey.border}`,
  },
  title: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#333333',
    wordBreak: 'break-word',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: theme.color.grey.neutral300,
    marginTop: 4,
  },
  dot: {
    display: 'inline-flex',
    flexShrink: 0,
    marginRight: 6,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    '& > * + *': {
      marginTop: 20,
    },
  },
  issue: {
    fontSize: '13px',
    marginTop: 4,
    '& b': {
      fontWeight: 600,
    },
  },
  sessionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sessionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#333333',
  },
  removeLink: {
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 600,
    color: theme.color.grey.neutral300,
    padding: '2px 6px',
    minWidth: 'auto',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    '& > * + *': {
      marginTop: 10,
    },
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: theme.color.grey.neutral500,
  },
  pair: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      width: 'calc(50% - 5px)',
    },
    '& > *:nth-child(odd)': {
      marginRight: 10,
    },
    '& > *:nth-child(n+3)': {
      marginTop: 10,
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    '& > * + *': {
      marginTop: 4,
    },
  },
  label: {
    fontSize: '12px',
    color: theme.color.grey.neutral300,
  },
  input: {
    font: 'inherit',
    fontSize: '13px',
    color: '#333333',
    padding: '8px 10px',
    border: '1px solid #D9D9D9',
    borderRadius: 4,
    backgroundColor: '#ffffff',
    width: '100%',
    boxSizing: 'border-box',
    '&:focus': {
      outline: 'none',
      borderColor: theme.color.secondary.main900,
      boxShadow: `0 0 0 3px ${theme.color.secondary.main50}`,
    },
    '&:disabled': {
      backgroundColor: '#FAFAFA',
      color: theme.color.grey.neutral200,
    },
  },
  inputError: {
    borderColor: theme.palette.error.main,
  },
  hint: {
    fontSize: '12px',
    color: theme.palette.error.main,
  },
  addressButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 400,
    color: '#333333',
    border: '1px solid #D9D9D9',
    borderRadius: 4,
    padding: '7px 10px',
  },
  session: {
    border: `1px solid ${theme.color.grey.border}`,
    borderRadius: 6,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    '& > * + *': {
      marginTop: 10,
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderTop: `1px solid ${theme.color.grey.border}`,
  },
  textButton: getToolbarButtonStyle(theme),
  primaryButton: getPrimaryButtonStyle(theme),
}));

interface FieldProps {
  rowId: string;
  column: ColumnDef;
  label?: string;
  value: string;
  error?: string;
  disabled: boolean;
  programNames: string[];
  reportTypeNames: string[];
  onChange: (rowId: string, key: string, value: string) => void;
}

const DrawerField = ({
  rowId,
  column,
  label,
  value,
  error,
  disabled,
  programNames,
  reportTypeNames,
  onChange,
}: FieldProps) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(value);
  const id = `drawer-${column.key}`;
  const options = getColumnOptions(column, programNames, reportTypeNames);
  const inputClassName = `${classes.input} ${error ? classes.inputError : ''}`;

  useEffect(() => setDraft(value), [value, rowId]);

  const commit = (next: string) => {
    if (next.trim() !== value) onChange(rowId, column.key, next.trim());
  };

  return (
    <div className={classes.field}>
      <Typography component='label' htmlFor={id} className={classes.label}>
        {label ?? column.header}
      </Typography>
      {options ? (
        <select
          id={id}
          className={inputClassName}
          value={value}
          disabled={disabled}
          onChange={(e) => commit(e.target.value)}
        >
          <option value=''>{column.kind === 'bool' ? 'Default' : ''}</option>
          {(value && !options.includes(value) ? [value, ...options] : options).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : column.kind === 'longtext' ? (
        <textarea
          id={id}
          className={inputClassName}
          rows={3}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
        />
      ) : (
        <input
          id={id}
          className={inputClassName}
          value={draft}
          disabled={disabled}
          placeholder={column.help}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(draft);
          }}
        />
      )}
      {error && <span className={classes.hint}>{error}</span>}
    </div>
  );
};

interface Props {
  row: ImportActivityRow;
  errors: RowErrors;
  programNames: string[];
  reportTypeNames: string[];
  locked?: boolean;
  onChange: (rowId: string, key: string, value: string) => void;
  onAddressClick: (rowId: string) => void;
  onAddSession: (rowId: string) => void;
  onRemoveSession: (rowId: string, n: number) => void;
  onAddOneOff: (rowId: string) => void;
  onRemoveOneOff: (rowId: string, n: number) => void;
  onDelete: (rowId: string) => void;
  onClose: () => void;
}

const byGroup = (group: string) => ACTIVITY_COLUMNS.filter((column) => column.group === group);

const findColumn = (key: string) => {
  const oneOffMatch = key.match(/^session_oneoff_(\d+)/);
  if (oneOffMatch) return getOneOffColumns(Number(oneOffMatch[1])).find((column) => column.key === key);
  return (
    [NAME_COLUMN, ...ACTIVITY_COLUMNS].find((column) => column.key === key) ??
    getSessionColumns(Number(key.match(/^session_(\d+)/)?.[1] ?? 1)).find((column) => column.key === key)
  );
};

export function ActivityDrawer({
  row,
  errors,
  programNames,
  reportTypeNames,
  locked,
  onChange,
  onAddressClick,
  onAddSession,
  onRemoveSession,
  onAddOneOff,
  onRemoveOneOff,
  onDelete,
  onClose,
}: Props) {
  const classes = useStyles();
  const activityType = parseActivityType(row.values.activityType);
  const readOnly =
    locked || row.createStatus === 'created' || row.createStatus === 'creating' || Boolean(row.activityId);
  const status = getRowStatus(row, Object.keys(errors).length);
  const rowSessions = getRowSessionCount(row);
  const rowOneOffs = getRowOneOffCount(row);
  const showOneOffs = !activityType || ONE_OFF_TYPES.includes(activityType) || rowOneOffs > 0;
  const alertColors = getAlertCardColors('warning');

  const field = (column: ColumnDef, label?: string) => (
    <DrawerField
      key={column.key}
      rowId={row.id}
      column={column}
      label={label}
      value={row.values[column.key] ?? ''}
      error={errors[column.key]}
      disabled={readOnly}
      programNames={programNames}
      reportTypeNames={reportTypeNames}
      onChange={onChange}
    />
  );

  const applicable = (columns: ColumnDef[]) =>
    columns.filter((column) => !activityType || isColumnApplicable(column, activityType));

  const [typeColumn, programColumn, descriptionColumn, tagsColumn] = [
    'activityType',
    'program',
    'description',
    'tags',
  ].map((key) => findColumn(key) as ColumnDef);
  const scheduleColumns = applicable(byGroup('Schedule'));
  const settingsColumns = byGroup('Settings');

  const issueEntries = Object.keys(errors).map((key) => {
    const column = findColumn(key);
    const sessionMatch = key.match(/^session_(\d+)/);
    const oneOffMatch = key.match(/^session_oneoff_(\d+)/);
    const label = sessionMatch
      ? `Session ${sessionMatch[1]} ${column?.header.toLowerCase()}`
      : oneOffMatch
      ? `One-off ${oneOffMatch[1]} ${column?.header.toLowerCase()}`
      : column?.header;
    return { key, label, message: errors[key] };
  });

  return (
    <aside className={classes.drawer} aria-label='Activity details'>
      <div className={classes.header}>
        <Box flex={1} minWidth={0}>
          <Typography className={classes.title}>{row.values.activityName || 'Untitled activity'}</Typography>
          <div className={classes.meta}>
            <span className={classes.dot}>
              <StatusIcon kind={status.kind} size={14} />
            </span>
            {activityType ? `${activityType.charAt(0).toUpperCase()}${activityType.slice(1)} · ` : ''}
            {status.label}
          </div>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close details'>
          <Close fontSize='small' />
        </IconButton>
      </div>

      <div className={classes.body}>
        {issueEntries.length > 0 && (
          <AlertCard
            variant='warning'
            title={`${issueEntries.length} ${issueEntries.length === 1 ? 'issue' : 'issues'} to fix`}
          >
            {issueEntries.map((entry) => (
              <Typography key={entry.key} className={classes.issue} style={{ color: alertColors.darkTextColor }}>
                <b>{entry.label}:</b> {entry.message}
              </Typography>
            ))}
          </AlertCard>
        )}

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Details</Typography>
          {field(NAME_COLUMN)}
          <div className={classes.pair}>
            {field(typeColumn)}
            {field(programColumn)}
          </div>
          {field(tagsColumn)}
          {field(descriptionColumn)}
        </div>

        {scheduleColumns.length > 0 && (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Schedule</Typography>
            <div className={classes.pair}>{scheduleColumns.map((column) => field(column))}</div>
          </div>
        )}

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Location</Typography>
          <Button
            className={`${classes.addressButton} ${errors.address || errors.remoteDetails ? classes.inputError : ''}`}
            disabled={readOnly}
            startIcon={
              row.values.remoteDetails && !row.values.address ? (
                <Language style={{ fontSize: 16 }} />
              ) : row.geocodeStatus === 'resolved' ? (
                <LocationOn style={{ fontSize: 16, color: '#4caf50' }} />
              ) : (
                <Search style={{ fontSize: 16 }} />
              )
            }
            onClick={() => onAddressClick(row.id)}
          >
            {row.values.address ||
              (row.values.remoteDetails ? `Remote: ${row.values.remoteDetails}` : 'Set a location')}
          </Button>
          {(errors.address || errors.remoteDetails) && (
            <span className={classes.hint}>{errors.address || errors.remoteDetails}</span>
          )}
        </div>

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Sessions</Typography>
          {Array.from({ length: rowSessions }, (_unused, i) => {
            const [name, desc, start, end, min, max, reportType, individualReports, roles] = getSessionColumns(i + 1);
            return (
              <div key={name.key} className={classes.session}>
                <div className={classes.sessionHeader}>
                  <Typography className={classes.sessionTitle}>Session {i + 1}</Typography>
                  {!readOnly && rowSessions > 1 && (
                    <Button className={classes.removeLink} onClick={() => onRemoveSession(row.id, i + 1)}>
                      Remove
                    </Button>
                  )}
                </div>
                {field(name, 'Name')}
                {field(desc)}
                <div className={classes.pair}>
                  {field(start)}
                  {field(end)}
                  {field(min, 'Min volunteers')}
                  {field(max, 'Max volunteers')}
                </div>
                {field(reportType)}
                <div className={classes.pair}>
                  {field(individualReports)}
                  {field(roles)}
                </div>
              </div>
            );
          })}
          {!readOnly && (
            <Button
              className={classes.textButton}
              style={{ alignSelf: 'flex-start' }}
              startIcon={<Add style={BUTTON_ICON_STYLE} />}
              onClick={() => onAddSession(row.id)}
            >
              Add session
            </Button>
          )}
        </div>

        {showOneOffs && (
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>One-off sessions</Typography>
            {Array.from({ length: rowOneOffs }, (_unused, i) => {
              const [dates, name, desc, start, end, min, max, reportType] = getOneOffColumns(i + 1);
              return (
                <div key={dates.key} className={classes.session}>
                  <div className={classes.sessionHeader}>
                    <Typography className={classes.sessionTitle}>One-off {i + 1}</Typography>
                    {!readOnly && (
                      <Button className={classes.removeLink} onClick={() => onRemoveOneOff(row.id, i + 1)}>
                        Remove
                      </Button>
                    )}
                  </div>
                  {field(dates)}
                  {field(name, 'Name')}
                  {field(desc)}
                  <div className={classes.pair}>
                    {field(start)}
                    {field(end)}
                    {field(min, 'Min volunteers')}
                    {field(max, 'Max volunteers')}
                  </div>
                  {field(reportType)}
                </div>
              );
            })}
            {!readOnly && (
              <Button
                className={classes.textButton}
                style={{ alignSelf: 'flex-start' }}
                startIcon={<Add style={BUTTON_ICON_STYLE} />}
                onClick={() => onAddOneOff(row.id)}
              >
                Add one-off session
              </Button>
            )}
          </div>
        )}

        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Settings</Typography>
          <div className={classes.pair}>{settingsColumns.map((column) => field(column))}</div>
        </div>
      </div>

      <div className={classes.footer}>
        <Button className={classes.textButton} disabled={readOnly} onClick={() => onDelete(row.id)}>
          Remove from import
        </Button>
        <Button className={classes.primaryButton} onClick={onClose}>
          Done
        </Button>
      </div>
    </aside>
  );
}
